import { useMemo } from 'react';
import Icon from '~/components/Icon/Icon';
import EditorButton from '~/components/editor/EditorButton/EditorButton';
import EditorCheckbox from '~/components/editor/EditorCheckbox/EditorCheckbox';
import EditorSlider from '~/components/editor/EditorSlider/EditorSlider';
import AtomProperties from '~/components/properties/AtomProperties/AtomProperties';
import Collapsible from '~/components/widgets/Collapsible/Collapsible';
import { useExperiment } from '~/systems/experiment';
import { useUiState } from '~/systems/uiState';
import './RegisterControl.css';

interface RegisterControlProps {
  initialState: boolean;
}

export default function RegisterControl({
  initialState
}: RegisterControlProps) {
  const {
    atomSelection,
    previewAmplitude,
    setPreviewAmplitude,
    setShowAtomsInteractions,
    showAtomsInteractions
  } = useUiState();
  const { channels, atoms, device, createAtom } = useExperiment();

  const maxAmplitude = useMemo(
    () => Math.max(...channels.map((c) => c.maxAmp)) / Math.PI,
    [channels]
  );

  const atomCapReached = useMemo(
    () => atoms.length >= (device?.maxAtomNum || 0),
    [atoms, device]
  );

  return (
    <div className="RegisterControl">
      <Collapsible
        initialState={initialState}
        button={() => (
          <h3 className="RegisterControl-sectionTitle">
            <Icon kind="atom" />
            Register
          </h3>
        )}
        items={
          <div className="RegisterControl-sectionBody">
            <EditorSlider
              label="Preview amplitude"
              name="preview-amplitude"
              value={previewAmplitude / Math.PI}
              min={0.1}
              step={0.1}
              max={maxAmplitude}
              divisions={[0.25, 0.5, 1, 2, 4, 8, 12, 16]}
              snapRange={0.02}
              logarithmic
              unit="𝝅"
              onChange={(v) => setPreviewAmplitude(v * Math.PI)}
            />
            <EditorCheckbox
              label="Show Atoms Interactions"
              name="show-atoms-interaction"
              value={showAtomsInteractions}
              onChange={(v) => setShowAtomsInteractions(v)}
            />
            <EditorButton
              disabled={atomCapReached}
              onClick={() => createAtom()}
            >
              Add new atom
            </EditorButton>
            <div className="RegisterControl-atomsList">
              {[...atomSelection].map((uuid) => (
                <AtomProperties key={uuid} atomUuid={uuid} />
              ))}
            </div>
          </div>
        }
      />
    </div>
  );
}
